import { CouponFrequency } from "./termDeposits/coupon-frequency.enum";
import { RolloverOptions } from "./termDeposits/rollover-options.enum";
import { SecuritySubType, SecurityType } from "./holdings";

export interface IOrderBase {
    Name: string;
    SecurityId: number;
    SettlementAcountId?: number;
    TradeAccountId?: number;
    DestinationId?: number;
    ExternalReference?: string;
    SettleInCurrencyCode?: string;
}
export interface CreateRedemptionOrderPayload extends IOrderBase {
    Quantity: number;
    SettlementDate: Date;
}

export interface EquityOrderPayload extends IOrderBase {
    Quantity?: number;
    Amount?: number;
    Action: OrderAction;
    Currency?: string;
}

export interface CreateApplicationPayload extends IOrderBase {
    Amount: Amount;
}
export interface Amount {
    value: number;
    currency: string;
    currencySymbol: string;
}

export interface BuyPayload {
    portfolioId: number;
    buyOrder: CreateApplicationPayload;
}
export interface SellPayload {
    portfolioId: number;
    sellOrder: CreateRedemptionOrderPayload;
}

export interface CancelOrderPayload {
    order: Order;
    portfolioId: number;
    instructionId: number;
}

export interface ApproveOrderPayload {
    order: Order;
    portfolioId: number;
    instructionId: number;
}

export interface Product {
    id: number;
    securityId: number;
    securityName: string;
    securityCurrency: string;
    productDisclosureLinkToken?: string;
    productDisclosureMyDocumentId: number;
    description: string;
    sector: string;
    managerName: string;
    fees: number;
    feeKiwiSaverDisplay: number;
    feeDisplay: number;
    productFees: any[];
    securityType: string;
    securitySubType: string;
    securityTypeValue: SecurityType;
    securitySubTypeValue: SecuritySubType;
    factSheetUrl: string;
    buyFees: number;
}

export interface ProductFee {
    availableProductId: number;
    securityId: number;
    fee: Amount | null;
    feeDescription: string;
}

export interface Order {
    id: number;
    orderId: number;
    status: number;
    statusName: string;
    createdAt: Date;
    data: OrderData;
    description: string;
    approvals: OrderApproval[];
    approvalsOutstanding: number;
    portfolioId: number;
}

export interface OrderData {
    securityId: number;
    amount: Amount;
    estimatedAmount: Amount;
    securityName: string;
    description: string;
    quantity: number;
    action: number;
    orderType: number;
    currency: string;
}

export interface OrderApproval {
    id: number;
    approvedById: number;
    approvedByName: string;
    isApproved: true;
    timestamp: Date;
}

export enum OrderAction {
    Buy = 0,
    Sell = 1,
}

export enum OrderType {
    Equity = 0,
    FOREX = 1,
    Bond = 2,
    Option = 3,
    Futures = 4,
    Fund = 5,
    TermDeposit = 6,
    PrivateEquity = 7
}

export interface Investment {
    SecurityId: number;
    SecurityName: string;
    Qty: string;
    SecurityType: number;
    MarketValue: {
        Value: number;
        CurrencyCode: string;
    };
    RefMarketValue: {
        Value: number,
        CurrencyCode: string;
    }
    CurrencyCode: string;
    RefCurrencyCode: string;
    AccountSubType: number;
    Price: number;
    SecuritySubType: number;
    FxRate: number;
}

export enum PlanType {
    ExplicitAmount = 0,
    AvailableCash = 1,
    GoalSeeker = 2
}

export interface InvestmentPlanStartPayload {
    periodType: number;
    positions: InvestmentPlanPosition[];
    amountValue: number;
    portfolioId: number;
    totalPercentageValue: number;
    startDate: Date;
}

export interface InvestmentPlan {
    periodType: number;
    positions: InvestmentPlanPosition[];
    amountValue: number;
    endDate?: Date;
    id?: number;
    modelPortfolioId?: number;
    nextDate?: Date;
    portfolioId: number;
    startDate: Date;
    status?: number;
    totalPercentageValue: number;
    planType: PlanType;
    fees: number;
    initiateRebalance: boolean;
}

export enum investmentPlanPeriodTypes {
    Weekly = 1,
    Fortnightly = 5,
    Monthly = 2,
    Quarterly = 3,
    SixMonthly = 4
}

export const regularPeriodPlanTypes = {
    ...investmentPlanPeriodTypes,
    DoNotRepeat: 0
}

export const initialInvestmentPlan: InvestmentPlan = {
    periodType: investmentPlanPeriodTypes.Monthly,
    positions: [],
    amountValue: 0,
    nextDate: null,
    portfolioId: null,
    startDate: null,
    totalPercentageValue: 0,
    planType: PlanType.AvailableCash,
    fees: 0,
    initiateRebalance: false
};


export interface InvestmentPlanPosition {
    id?: number;
    modelPortfolioId?: number;
    securityId: number;
    managerName: string;
    securityName: string;
    fees: number;
    sector: string;
    percent: number; // this goes from 0.00 to 1
}



export enum RegularInvestmentStatus {
    Incomplete = 0, // Plan is saved in an incomplete state and cannot be made complete yet
    Ready = 1,   // Plan is completed and can be made active (i.e. started)
    Active = 2,     // Plan has been started and is active
    InActive = 3,    // Plan has been stopped and is inactive
}

export interface OrderNotification {
    ordersToAuthorize: number;
    orders: Order[];
}

export enum InstructionStatus {
    Created = 0,
    ApprovalRequired = 1,
    Pending = 2,
    Processing = 3,
    Completed = 4,
    Cancelled = 5,
    Failed = 6,
    ReviewRequired = 7,
    ReviewFailed = 8
}

export interface TermDepositOrderPayload {
    amount: number;
    durationMonths: number;
    couponFrequency: CouponFrequency;
    rateCardId: number;
    expectedRate: number;
    rolloverOptions: RolloverOptions;
    currency: string;
    bankName: string;
    durationName: string;
}
